import React, { FC, useContext, useEffect, useState, useCallback } from "react";
import clsx from "clsx";

import { Context } from "../../../../managerStore";
import { RegistrationSubject } from "../../../../state";
import { ModalContext } from "../../../../libs/ModalProvider";
import Modal from "../../../../atoms/SubjectRegistration/Modal";
import ToolTip from "../../../../atoms/ToolTip";
import Confirm from "../../../../molecules/Confirm";
import { ReactComponent as CheckOnIcon } from "../../../../assets/icon/check_on.svg";
import { ReactComponent as CheckOffIcon } from "../../../../assets/icon/check_off.svg";
import { ReactComponent as EditIcon } from "../../../../assets/icon/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../../assets/icon/delete.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icon/close.svg";
import { ReactComponent as CloseCircleIcon } from "../../../../assets/icon/close_circle.svg";
import { ReactComponent as CheckCircleIcon } from "../../../../assets/icon/check_circle.svg";

import styles from "./SubjectSelectModal.module.scss";

type Props = {
  calling: boolean;
  registrationSubjects: RegistrationSubject[];
  onClose: () => void;
  selectSubjects: (ids: number[]) => void;
  submitSubject: (name: string) => void;
  updateSubject: (name: string, id: number) => void;
  deleteSubject: (id: number) => void;
};

const SubjectSelectModal: FC<Props> = ({
  calling,
  registrationSubjects,
  onClose,
  selectSubjects,
  submitSubject,
  updateSubject,
  deleteSubject,
}) => {
  const { state } = useContext(Context);
  const ctx = useContext(ModalContext);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState<number[]>([]);
  const [originalText, setOriginalText] = useState<string>("");
  const [openEditors, setOpenEditors] = useState<Set<number>>(new Set());
  const [editTexts, setEditTexts] = useState<Record<number, string>>({});

  const isRegistered = useCallback(
    (subjectId: number) =>
      state.teacher_subjects_by_teacher.subjects.some(
        data => data.subject_id === subjectId,
      ),
    [state.teacher_subjects_by_teacher.subjects],
  );

  const subjectsByCategory = useCallback(
    (categoryId: number) =>
      state.subjects.filter(e => e.category_id === categoryId),
    [state.subjects],
  );

  const closeEditor = useCallback(
    (subjectId: number) => {
      setOpenEditors(prev => {
        const next = new Set(prev);
        next.delete(subjectId);
        return next;
      });
      setEditTexts(prev => {
        const { [subjectId]: _omit, ...rest } = prev;
        return rest;
      });
    },
    [setOpenEditors, setEditTexts],
  );

  useEffect(() => {
    setSelectedSubjectIds(
      registrationSubjects.map(({ subject_id }) => subject_id),
    );
  }, [registrationSubjects]);

  return (
    <Modal className={styles.content} onClose={onClose}>
      <button className={styles.closeButton} onClick={onClose}>
        <CloseIcon className={styles.closeIcon} />
      </button>
      <div className={styles.subject}>
        {state.subject_categories.map(category => (
          <div key={category.id} className={styles.block}>
            <p className={styles.category}>{category.name}</p>
            <ul className={styles.list}>
              {subjectsByCategory(category.id).map(subject => {
                const isEditing = openEditors.has(subject.id);
                return (
                  <li
                    key={subject.id}
                    className={clsx(
                      styles.item,
                      subject.school_id && styles.isOriginal,
                    )}
                  >
                    {!isEditing && (
                      <>
                        <label
                          className={clsx(
                            isRegistered(subject.id) ? styles.isDisabled : "",
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSubjectIds.includes(subject.id)}
                            disabled={isRegistered(subject.id)}
                            onChange={() => {
                              setSelectedSubjectIds(prev => {
                                const isSelected = prev.includes(subject.id);
                                return isSelected
                                  ? prev.filter(p => p !== subject.id)
                                  : [...prev, subject.id];
                              });
                            }}
                          />
                          <span className={styles.checkMark}>
                            <CheckOnIcon className={styles.checkOnIcon} />
                            <CheckOffIcon className={styles.checkOffIcon} />
                          </span>
                          {subject.name}
                        </label>
                        {subject.school_id && (
                          <div className={styles.buttons}>
                            <ToolTip
                              content="この教科に紐づくデータがあるため削除できません"
                              isEnabled={!subject.is_deletable}
                            >
                              <button
                                type="button"
                                disabled={calling || !subject.is_deletable}
                                onClick={() => {
                                  ctx.actions.showModal(
                                    <Confirm
                                      calling={calling}
                                      closeModal={() => {
                                        ctx.actions.closeModal();
                                      }}
                                      variant="danger"
                                      content={
                                        <>
                                          削除しますか？
                                          <br />
                                          この操作は取り消せません。
                                        </>
                                      }
                                      callback={() => {
                                        deleteSubject(subject.id);
                                        ctx.actions.closeModal();
                                      }}
                                    />,
                                  );
                                }}
                              >
                                <DeleteIcon className={styles.deleteIcon} />
                              </button>
                            </ToolTip>
                            <button
                              type="button"
                              onClick={() => {
                                setOpenEditors(prev => {
                                  const next = new Set(prev);
                                  next.add(subject.id);
                                  return next;
                                });
                                setEditTexts(prev => ({
                                  ...prev,
                                  [subject.id]:
                                    prev[subject.id] ?? subject.name ?? "",
                                }));
                              }}
                            >
                              <EditIcon className={styles.editIcon} />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                    {subject.school_id && isEditing && (
                      <div className={styles.editor}>
                        <input
                          className={styles.inputText}
                          type="text"
                          value={editTexts[subject.id] ?? ""}
                          onChange={e => {
                            const value = e.target.value;
                            setEditTexts(prev => ({
                              ...prev,
                              [subject.id]: value,
                            }));
                          }}
                        />
                        <div className={styles.buttons}>
                          <button
                            type="button"
                            onClick={() => {
                              closeEditor(subject.id);
                            }}
                          >
                            <CloseCircleIcon />
                          </button>
                          <button
                            type="button"
                            disabled={calling}
                            onClick={() => {
                              const text = (editTexts[subject.id] ?? "").trim();
                              if (text) updateSubject(text, subject.id);
                              closeEditor(subject.id);
                            }}
                          >
                            <CheckCircleIcon />
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className={styles.footer}>
        <p className={styles.note}>
          上記にない教科は、ここで自由に登録できます。例：野球部、美化委員会、特進英語など
        </p>
        <div className={styles.block}>
          <input
            className={styles.textInput}
            type="text"
            value={originalText}
            onChange={e => setOriginalText(e.target.value)}
            placeholder="教科名"
          />
          <button
            className={styles.addButton}
            type="button"
            disabled={originalText.trim() === "" || calling}
            onClick={() => {
              const name = originalText.trim();
              if (name) submitSubject(name);
              setOriginalText("");
            }}
          >
            追加
          </button>
        </div>
        <div className={styles.block}>
          <button
            className={styles.enterButton}
            type="button"
            onClick={() => {
              onClose();
              selectSubjects(selectedSubjectIds);
            }}
          >
            決定
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SubjectSelectModal;
